import PropTypes from 'prop-types'
import React from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getMaterielIconValue } from '../../../../utils/MaterielUtils'
import { ArrowForward, Clear, CompareArrows, NoteAdd } from '@mui/icons-material'
import { Card, CardContent, Grid, Icon, Tooltip, Typography } from '@mui/material'
import { MATERIEL_STATE_ON_SITE_ID } from 'materiel/constants/MaterielStateConstants'


const MaterielCard = ({
    newSituations,
    materielCategory,
    materielTypeName,
    materiel,
    situationDate,
    undoMateriel,
    actions,
}) => {
    const {
        centrals,
        centralTypes,
        powerSupplies,
        powerSupplyTypes,
        sensors,
        sensorTypes,
        sims,
        simTypes,
        telecoms,
        telecomTypes,
        variousMateriels,
        variousMaterielTypes,
        equipments,
        equipmentTypes,
        subscriptions,
        subscriptionTypes,
        simSubscriptionsLastSituations,
        materielStates,
    } = useSelector(store => ({
        centrals: store.CentralReducer.centrals,
        centralTypes: store.CentralReducer.centralTypes,
        powerSupplies: store.PowerSupplyReducer.powerSupplies,
        powerSupplyTypes: store.PowerSupplyReducer.powerSupplyTypes,
        sensors: store.SensorReducer.sensors,
        sensorTypes: store.SensorReducer.sensorTypes,
        sims: store.SimReducer.sims,
        simTypes: store.SimReducer.simTypes,
        telecoms: store.TelecomReducer.telecoms,
        telecomTypes: store.TelecomReducer.telecomTypes,
        variousMateriels: store.VariousMaterielReducer.variousMateriels,
        variousMaterielTypes: store.VariousMaterielReducer.variousMaterielTypes,
        equipments: store.EquipmentReducer.equipments,
        equipmentTypes: store.EquipmentReducer.equipmentTypes,
        subscriptions: store.SubscriptionReducer.subscriptions,
        subscriptionTypes: store.SubscriptionReducer.subscriptionTypes,
        simSubscriptionsLastSituations: store.SubscriptionReducer.simSubscriptionsLastSituations,
        materielStates: store.MaterielReducer.materielStates,
    }), shallowEqual)

    const getLabelSimAssociated = idSub => {
        const simSubscription = simSubscriptionsLastSituations?.find(({ idSubscription }) => idSubscription === idSub)
        if (simSubscription) {
            const sim = sims?.find(({ id }) => id === simSubscription.idSim)
            if (sim) {
                const type = simTypes?.find(({ materielType }) => materielType === sim.materielType)
                return `${type ? `${type.name}: ` : ''} ${sim.serialNumber}`
            }
        }
        return ''
    }

    const {
        remove,
        replace,
        add,
        cancel,
    } = actions

    const newSitCurrentMat = materiel ? newSituations?.find(s => s.materielCategory === materielCategory && s.materielId === materiel.id) : undefined
    const newSitNextMat = materiel ? newSituations?.find(s => s.materielCategory === materielCategory && s.previousMateriel === materiel.id) : undefined
    const materielsByCategory = {
        central: { materiels: centrals, types: centralTypes },
        powerSupply: { materiels: powerSupplies, types: powerSupplyTypes },
        sensor: { materiels: sensors, types: sensorTypes },
        sim: { materiels: sims, types: simTypes },
        telecom: { materiels: telecoms, types: telecomTypes },
        variousMateriel: { materiels: variousMateriels, types: variousMaterielTypes },
        equipment: { materiels: equipments, types: equipmentTypes },
        subscription: { materiels: subscriptions, types: subscriptionTypes },
    }
    const { materiels: categoryMateriels = [], types: categoryTypes = [] } = materielsByCategory[materielCategory] ?? {}
    const newMateriel = newSitNextMat ? categoryMateriels.find(m => m.id === newSitNextMat.materielId) : null
    const newMaterielType = newMateriel?.materielType ? categoryTypes.find(t => t.materielType === newMateriel.materielType) : null

    return (
        <Card>
            <CardContent>
                <Grid container spacing={1} alignItems='center'>
                    <Grid>
                        <Tooltip title={i18n[materielCategory]}>
                            <Icon fontSize='large'>{getMaterielIconValue(materielCategory, 'large')}</Icon>
                        </Tooltip>
                    </Grid>
                    <Grid container size={'grow'}>
                        <Grid size={6}>
                            <Typography variant='h6'>{materiel ? `${materielTypeName ? `${materielTypeName} -` : ''} ${materiel.numLine || materiel.imei || materiel.serialNumber || ''}` : i18n.noMateriel}</Typography>
                        </Grid>
                        <Grid size={6}>
                            <Typography variant='h6'>{materiel && situationDate ? `${i18n.onSiteSince} ${situationDate}` : ''}</Typography>
                        </Grid>
                        {materielCategory === 'subscription' && (
                            <Typography variant='h6'>{getLabelSimAssociated(materiel.id)}</Typography>
                        )}
                        {newSitCurrentMat && newSitCurrentMat?.statusCode !== MATERIEL_STATE_ON_SITE_ID && (
                            <Grid container size={12}>
                                <Grid size={6} color={'red'}>
                                    <Clear />
                                    <Typography variant='h6'>{(materielStates?.find(matState => matState.code === newSitCurrentMat.statusCode) || {}).label}</Typography>
                                </Grid>
                                {newMateriel && (
                                    <Grid size={6} color={'dodgerblue'}>
                                        <ArrowForward />
                                        <Typography variant='h6'>{`${newMaterielType ? `${newMaterielType.name} - ` : ''}${newMateriel.numLine || newMateriel.imei || newMateriel.serialNumber || ''}`}</Typography>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>
                    <Grid>
                        {
                            add && (
                                <Tooltip title={i18n.add}>
                                    <NoteAdd
                                        sx={{ cursor: 'pointer' }}
                                        fontSize='large'
                                        onClick={() => add(materielCategory)}
                                    />
                                </Tooltip>
                            )
                        }
                        {
                            !newSitCurrentMat && replace && (
                                <Tooltip title={i18n.replace}>
                                    <CompareArrows
                                        sx={{ cursor: 'pointer' }}
                                        fontSize='large'
                                        onClick={() => replace(materielCategory)}
                                    />
                                </Tooltip>
                            )
                        }
                        {
                            !newSitCurrentMat && remove && (
                                <Tooltip title={i18n.remove}>
                                    <Clear
                                        sx={{ cursor: 'pointer' }}
                                        fontSize='large'
                                        onClick={() => remove(materielCategory)}
                                    />
                                </Tooltip>
                            )
                        }
                        {
                            newSitCurrentMat && (
                                <Tooltip title={i18n.cancel}>
                                    <Clear
                                        sx={{ cursor: 'pointer' }}
                                        fontSize='large'
                                        onClick={() => {
                                            if (cancel) {
                                                cancel()
                                            } else {
                                                undoMateriel(materielCategory, newSitCurrentMat, newSitNextMat)
                                            }
                                        }}
                                    />
                                </Tooltip>
                            )
                        }
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

MaterielCard.propTypes = {
    newSituations: PropTypes.arrayOf(PropTypes.object),
    materielCategory: PropTypes.string,
    materielTypeName: PropTypes.string,
    materiel: PropTypes.object,
    situationDate: PropTypes.string,
    actions: PropTypes.object,
    undoMateriel: PropTypes.func,
}

export default MaterielCard